import React from 'react';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import PersonalisationComponentSlot from './PersonalisationComponentSlot.jsx';
import PersonalisationAlphanumericSlot from './PersonalisationAlphanumericSlot.jsx';
import PersonalisationPageContentSlot from './PersonalisationPageContentSlot.jsx';
import PersonalisationStarMapSlot from './PersonalisationStarMapSlot.jsx';
import PersonalisationLocationMapSlot from './PersonalisationLocationMapSlot.jsx';
import PersonalisationSequentialSlot from './PersonalisationSequentialSlot.jsx';
import PersonalisationCheckboxSlot from './PersonalisationCheckboxSlot.jsx';
import { PersonalisationImageUploadSlot } from './PersonalisationImageUploadSlot.jsx';
import PersonalisationUvInsertSlot from './PersonalisationUvInsertSlot';
import PersonalisationGroupedSlot from './PersonalisationGroupedSlot';
import PersonalisationPairedSlot from './PersonalisationPairedSlot';

import { useViewModel } from './useViewModel';
import { processSlotHeading } from '../../utilities/processSlotHeading';
import stepStyles from './personalisation-step.module.css';
import slotStyles from './personalisation-slot.module.css';
import tabsStyles from './personalisation-tabs.module.css';
import 'react-tabs/style/react-tabs.css';

/**
 * PersonalisationStep component displays a step in the personalisation journey, including slots and input handling.
 *
 * @param {Object} props - Component props
 * @param {Object} props.step - The step object containing slot and summaryLabel
 * @returns {import('react').JSX.Element}
 */
export function PersonalisationStep() {
  const {
    slots,
    componentGroups,
    slotMode,
    showTabs,
    selections,
    updateSelections,
    hasPersonalisationOptions,
    createSlotRef,
    shouldEnableAutoScroll,
    shouldShowStarMapSlot,
    shouldShowLocationMapSlot,
    isPairedJourney
  } = useViewModel({});

  // Show empty state if no personalisation options are available.
  if (!hasPersonalisationOptions) {
    return (
      <div className={stepStyles['personalisation__step'] + ' ' + stepStyles['personalisation__step--empty']}>
        <h3 className="heading-2xl font-medium">No personalisation options available</h3>
        <p className="body-base">
          This product doesn't have any personalisation options to choose from. You can add it to your bag as-is.
        </p>
      </div>
    );
  }

  // Show sequential slot if the slot mode is SEQUENTIAL.
  if (slotMode === 'SEQUENTIAL') {
    return <PersonalisationSequentialSlot slots={slots} componentGroups={componentGroups} />;
  }

  // Show tabs if the slot mode is FIXED and showTabs is true.
  if (slotMode === 'FIXED' && showTabs) {
    return (
      <div className={stepStyles['personalisation__step']}>
        {showTabs && (
          <Tabs className={tabsStyles['personalisation__tabs']}>
            <TabList className={tabsStyles['personalisation__tabs-list']}>
              {slots.map((slot) => (
                <Tab
                  key={slot.id + 'tabList'}
                  selectedClassName={tabsStyles['personalisation__tabs-list-item--selected']}
                  className={
                    tabsStyles['personalisation__tabs-list-item'] +
                    ' ' +
                    tabsStyles['personalisation__tabs-list-item--wrapped'] +
                    ' body-xs body-uppercase font-medium'
                  }
                >
                  {processSlotHeading({
                    headingText: slot.uiHeading,
                    shouldAppend: false,
                    trimText: '(optional)',
                    isRequired: slot.isRequired
                  })}
                </Tab>
              ))}
            </TabList>
            {slots.map((slot) => (
              <TabPanel className={tabsStyles['personalisation__tabs-panel']} key={slot.id + 'tabPanel'}>
                <div key={slot.id} className={slotStyles['personalisation__slot']}>
                  {['COMPONENT', 'BASE_CONFIG'].includes(slot.slotType) && (
                    <PersonalisationComponentSlot slot={slot} componentGroups={componentGroups} />
                  )}
                  {slot.slotType === 'IMAGE_UPLOAD' && <PersonalisationImageUploadSlot slot={slot} />}
                  {slot.slotType === 'ALPHANUMERIC' && (
                    <PersonalisationAlphanumericSlot
                      slot={slot.id}
                      value={slot}
                      inputValue={selections.slots.find((s) => s.id === slot.id)?.selected || ''}
                      onChange={(id, newValue) => {
                        updateSelections({ slotId: id, itemSelected: newValue.text !== '' ? newValue : null });
                      }}
                    />
                  )}
                  {slot.slotType === 'STAR_MAP' && shouldShowStarMapSlot && <PersonalisationStarMapSlot slot={slot} />}
                  {slot.slotType === 'LOCATION_MAP' && shouldShowLocationMapSlot && (
                    <PersonalisationLocationMapSlot slot={slot} />
                  )}
                  {slot.slotType === 'CHECKBOX' && <PersonalisationCheckboxSlot slot={slot} />}
                </div>
              </TabPanel>
            ))}
          </Tabs>
        )}
      </div>
    );
  }

  // Show paired rows (a component slot plus the text slot at the same order index) if the slot mode is PAIRED.
  if (slotMode === 'PAIRED') {
    return <PersonalisationPairedSlot slots={slots} componentGroups={componentGroups} />;
  }

  if (slotMode === 'GROUPED') {
    return <PersonalisationGroupedSlot slots={slots} />;
  }

  // Show standard slot if the slot mode is FIXED and showTabs is false.
  return (
    <div className={stepStyles['personalisation__step']}>
      {slots.map((slot) => (
        <div
          key={slot.id}
          className={slotStyles['personalisation__slot']}
          ref={shouldEnableAutoScroll ? createSlotRef(slot.id) : null}
        >
          {['COMPONENT', 'BASE_CONFIG'].includes(slot.slotType) && (
            <PersonalisationComponentSlot slot={slot} componentGroups={componentGroups} />
          )}

          {slot.slotType === 'IMAGE_UPLOAD' && (
            <PersonalisationImageUploadSlot
              slot={slot}
              shouldShowStarMapSlot={shouldShowStarMapSlot}
              shouldShowLocationMapSlot={shouldShowLocationMapSlot}
            />
          )}

          {slot.slotType === 'ALPHANUMERIC' && (
            <PersonalisationAlphanumericSlot
              slot={slot.id}
              value={slot}
              inputValue={selections.slots.find((s) => s.id === slot.id)?.selected || ''}
              onChange={(id, newValue) => {
                const itemSelected = newValue.text !== '' ? newValue : null;
                updateSelections({ slotId: id, itemSelected });
              }}
              processHeading={!isPairedJourney}
            />
          )}
          {slot.slotType === 'PAGE_CONTENT' && <PersonalisationPageContentSlot slot={slot} />}
          {slot.slotType === 'STAR_MAP' && shouldShowStarMapSlot && <PersonalisationStarMapSlot slot={slot} />}
          {slot.slotType === 'LOCATION_MAP' && shouldShowLocationMapSlot && (
            <PersonalisationLocationMapSlot slot={slot} />
          )}
          {slot.slotType === 'CHECKBOX' && <PersonalisationCheckboxSlot slot={slot} />}
          {slot.slotType === 'UV_INSERT' && <PersonalisationUvInsertSlot slot={slot} />}
        </div>
      ))}
    </div>
  );
}
